import React, { useState } from "react";
import {
    Modal,
    Upload,
    Button,
    Steps,
    Card,
    Typography,
    Table,
    Tag,
    Alert,
    Descriptions,
    Space,
    Divider,
    Progress,
    message,
    Tooltip,
    Badge,
    Collapse
} from "antd";
import {
    InboxOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FileExcelOutlined,
    RocketOutlined,
    ArrowLeftOutlined,
    CodeOutlined,
    FunctionOutlined,
    FileTextOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { PostCaza } from "../../../../dao/jutge/PostCaza";
import { PutJutgeCazaTest } from "../../../../dao/jutge/PutJutgeCazaTest";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    // Si se abre dentro de un Conjunt de Pildores, la pildora importada se vincula
    // automaticamente a ese conjunt (tabla puente jutge.conjunt_caza).
    defaultConjunt?: number;
}

interface ParsedGeneral {
    nombre: string;
    descripcion: string;
    dificultad: string;
    nivel: number;
    vida: number;
    must: string;
    mustnot: string;
    url_teoria: string;
    rewardType: string;
    reward_valor?: number;
    intentos: number;
    estat: number;
}

interface ParsedStep {
    paso: number;
    titulo: string;
    instrucciones: string;
}

interface ParsedTest {
    paso: number;
    name: string;
    tipus: "es" | "method" | "content";
    entrada?: string;
    sortida?: string;
    methodName?: string;
    returnType?: string;
    modifiers?: string[];
    parameters?: string[];
    contentText?: string;
    mustContain?: boolean;
}

export const ImportarCazaExcelModal: React.FC<Props> = ({ visible, onClose, onSuccess, defaultConjunt }) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [parsedGeneral, setParsedGeneral] = useState<ParsedGeneral | null>(null);
    const [parsedSteps, setParsedSteps] = useState<ParsedStep[]>([]);
    const [parsedTests, setParsedTests] = useState<ParsedTest[]>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitProgress, setSubmitProgress] = useState<number>(0);
    const [statusMessage, setStatusMessage] = useState<string>("");

    const resetState = () => {
        setCurrentStep(0);
        setParsedGeneral(null);
        setParsedSteps([]);
        setParsedTests([]);
        setValidationErrors([]);
        setValidationWarnings([]);
        setIsSubmitting(false);
        setSubmitProgress(0);
        setStatusMessage("");
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetState();
            onClose();
        }
    };

    // 1. Download Template generator
    const handleDownloadTemplate = () => {
        try {
            const wb = XLSX.utils.book_new();

            // Sheet 1: General
            const generalData = [
                ["Campo", "Valor", "Descripcion / Opciones"],
                ["Nombre", "Mi Nueva Píldora de Java", "Título del ejercicio (Obligatorio)"],
                ["Descripcion", "Aprende los fundamentos de variables y funciones con este reto paso a paso.", "Descripción breve de la píldora"],
                ["Dificultad", "Beginner", "Beginner | Intermediate | Expert"],
                ["Intentos", 0, "Número de intentos (0 = Ilimitados, o número ej. 5, 10)"],
                ["URL_Teoria", "https://ejemplo.com/teoria-java", "Enlace a documentación o teoría explicativa (opcional)"],
                ["Tipo_Recompensa", "JAVAMON_RANDOM", "JAVAMON_RANDOM | PUNTUACION | JAVAMON"],
                ["Valor_Recompensa", 10, "Puntos si Tipo_Recompensa es PUNTUACION (o ID si es fijo)"],
                ["Must_Include", "Scanner, System.out.println", "Palabras o comandos que el alumno DEBE usar (separados por coma)"],
                ["Must_Not_Include", "while, for", "Palabras o comandos PROHIBIDOS para el alumno (separados por coma)"]
            ];
            const wsGeneral = XLSX.utils.aoa_to_sheet(generalData);
            wsGeneral["!cols"] = [{ wch: 18 }, { wch: 45 }, { wch: 55 }];
            XLSX.utils.book_append_sheet(wb, wsGeneral, "General");

            // Sheet 2: Pasos
            const pasosData = [
                ["Paso", "Titulo", "Instrucciones"],
                [1, "Estructura principal", "Crea una clase llamada Solucio con un método main que lea dos enteros y los sume."],
                [2, "Añadir función de cálculo", "Añade un método estático público llamado sumar que devuelva la suma de dos enteros."],
                [3, "Gestión de excepciones", "Valida que los números sean positivos, si no muestra un mensaje de error."]
            ];
            const wsPasos = XLSX.utils.aoa_to_sheet(pasosData);
            wsPasos["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 70 }];
            XLSX.utils.book_append_sheet(wb, wsPasos, "Pasos");

            // Sheet 3: Tests
            const testsData = [
                [
                    "Paso",
                    "Nombre",
                    "Tipo",
                    "Entrada",
                    "Salida",
                    "Nombre_Metodo",
                    "Tipo_Retorno",
                    "Modificadores",
                    "Parametros",
                    "Contenido",
                    "Debe_Contener"
                ],
                [
                    1,
                    "Test básico suma",
                    "es",
                    "2\n3",
                    "5\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    1,
                    "Test números negativos",
                    "es",
                    "-1\n4",
                    "Error: deben ser positivos\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    2,
                    "Test método sumar",
                    "method",
                    "4, 5",
                    "9",
                    "sumar",
                    "int",
                    "public, static",
                    "int a, int b",
                    "",
                    ""
                ],
                [
                    3,
                    "Verificar palabra Scanner",
                    "content",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "Scanner",
                    "SI"
                ]
            ];
            const wsTests = XLSX.utils.aoa_to_sheet(testsData);
            wsTests["!cols"] = [
                { wch: 8 },
                { wch: 25 },
                { wch: 12 },
                { wch: 20 },
                { wch: 25 },
                { wch: 18 },
                { wch: 14 },
                { wch: 18 },
                { wch: 18 },
                { wch: 25 },
                { wch: 14 }
            ];
            XLSX.utils.book_append_sheet(wb, wsTests, "Tests");

            // Sheet 4: Ayuda / Guía
            const ayudaData = [
                ["Guía de Formato de Plantilla Excel para Píldoras"],
                [""],
                ["1. Hoja 'General': Contiene los metadatos de la Píldora. No cambies los nombres de la columna 'Campo'."],
                ["2. Hoja 'Pasos': Define las etapas del ejercicio en orden secuencial (Paso 1, 2, ...)."],
                ["3. Hoja 'Tests': Define los tests asociados a cada paso."],
                ["   - Tipos de Test permitidos:"],
                ["       • 'es': Test de Entrada / Salida por consola estándar (requiere 'Entrada' y 'Salida')."],
                ["       • 'method': Test de llamada a método (requiere 'Nombre_Metodo', 'Tipo_Retorno', 'Entrada' y 'Salida')."],
                ["       • 'content': Comprobación estática de código (requiere 'Contenido' y 'Debe_Contener' SI/NO)."],
                ["4. Las imágenes (Javamon o fondo) se pueden añadir o editar posteriormente desde la interfaz."]
            ];
            const wsAyuda = XLSX.utils.aoa_to_sheet(ayudaData);
            XLSX.utils.book_append_sheet(wb, wsAyuda, "Instrucciones_Ayuda");

            XLSX.writeFile(wb, "Plantilla_Pildora_Caza.xlsx");
            message.success("Plantilla descargada correctamente");
        } catch (error) {
            console.error("Error al generar plantilla:", error);
            message.error("Error al descargar la plantilla Excel");
        }
    };

    // 2. Parse Excel file
    const processExcelFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const errors: string[] = [];
                const warnings: string[] = [];

                // --- 1. Parse General ---
                const generalSheetName = workbook.SheetNames.find(n => {
                    const clean = n.trim().toLowerCase();
                    return clean === "general" || clean === "configuracion" || clean === "configuración" || clean === "config";
                });
                let genObj: ParsedGeneral = {
                    nombre: "",
                    descripcion: "",
                    dificultad: "Beginner",
                    nivel: 1,
                    vida: 100,
                    must: "",
                    mustnot: "",
                    url_teoria: "",
                    rewardType: "JAVAMON_RANDOM",
                    reward_valor: undefined,
                    intentos: 0,
                    estat: 1
                };

                if (!generalSheetName) {
                    errors.push("Falta la hoja 'General' en el archivo Excel.");
                } else {
                    const genRows: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[generalSheetName], { header: 1 });
                    genRows.forEach(row => {
                        if (!row || row.length < 2) return;
                        const key = String(row[0] || "").trim().toLowerCase();
                        const val = row[1] !== undefined && row[1] !== null ? String(row[1]).trim() : "";

                        if (key === "nombre") genObj.nombre = val;
                        else if (key === "descripcion") genObj.descripcion = val;
                        else if (key === "dificultad") genObj.dificultad = val || "Beginner";
                        else if (key === "nivel") genObj.nivel = parseInt(val) || 1;
                        else if (key === "vida") genObj.vida = parseInt(val) || 100;
                        else if (key === "must_include" || key === "must") genObj.must = val;
                        else if (key === "must_not_include" || key === "mustnot") genObj.mustnot = val;
                        else if (key === "url_teoria" || key === "urlteoria") genObj.url_teoria = val;
                        else if (key === "intentos") genObj.intentos = isNaN(parseInt(val)) ? 0 : parseInt(val);
                        else if (key === "tipo_recompensa" || key === "rewardtype" || key === "reward_type") {
                            const upper = val.toUpperCase();
                            if (upper.includes("PUNTUACION") || upper.includes("PUNTOS")) genObj.rewardType = "PUNTUACION";
                            else if (upper === "JAVAMON") genObj.rewardType = "JAVAMON";
                            else genObj.rewardType = "JAVAMON_RANDOM";
                        }
                        else if (key === "valor_recompensa" || key === "reward_valor" || key === "puntos") {
                            genObj.reward_valor = parseInt(val) || 10;
                        }
                    });

                    if (!genObj.nombre) {
                        errors.push("El campo 'Nombre' en la hoja General es obligatorio.");
                    }
                }

                // --- 2. Parse Pasos ---
                const pasosSheetName = workbook.SheetNames.find(n => n.trim().toLowerCase() === "pasos");
                let stepsArr: ParsedStep[] = [];

                if (!pasosSheetName) {
                    errors.push("Falta la hoja 'Pasos' en el archivo Excel.");
                } else {
                    const pasosRows: any[] = XLSX.utils.sheet_to_json(workbook.Sheets[pasosSheetName]);
                    if (pasosRows.length === 0) {
                        errors.push("La hoja 'Pasos' no contiene ningún paso.");
                    } else {
                        pasosRows.forEach((row, idx) => {
                            const stepNum = parseInt(row["Paso"] || row["paso"] || idx + 1);
                            const titulo = String(row["Titulo"] || row["titulo"] || `Paso ${stepNum}`).trim();
                            const instrucciones = String(row["Instrucciones"] || row["instrucciones"] || "").trim();

                            if (!instrucciones) {
                                warnings.push(`El Paso ${stepNum} ("${titulo}") tiene las instrucciones vacías.`);
                            }

                            stepsArr.push({
                                paso: stepNum,
                                titulo: titulo,
                                instrucciones: instrucciones
                            });
                        });
                        // Order steps sequentially
                        stepsArr.sort((a, b) => a.paso - b.paso);
                    }
                }

                // --- 3. Parse Tests ---
                const testsSheetName = workbook.SheetNames.find(n => n.trim().toLowerCase() === "tests");
                let testsArr: ParsedTest[] = [];

                if (testsSheetName) {
                    const testsRows: any[] = XLSX.utils.sheet_to_json(workbook.Sheets[testsSheetName]);
                    testsRows.forEach((row, idx) => {
                        const stepNum = parseInt(row["Paso"] || row["paso"] || 1);
                        const rawType = String(row["Tipo"] || row["tipo"] || "es").trim().toLowerCase();
                        let tipus: "es" | "method" | "content" = "es";
                        if (rawType.includes("method")) tipus = "method";
                        else if (rawType.includes("content")) tipus = "content";

                        const name = String(row["Nombre"] || row["nombre"] || `Test ${idx + 1} (Paso ${stepNum})`).trim();
                        const entrada = row["Entrada"] !== undefined ? String(row["Entrada"]) : "";
                        const sortida = row["Salida"] !== undefined ? String(row["Salida"]) : "";
                        const methodName = row["Nombre_Metodo"] ? String(row["Nombre_Metodo"]).trim() : "";
                        const returnType = row["Tipo_Retorno"] ? String(row["Tipo_Retorno"]).trim() : "void";
                        const rawModifiers = row["Modificadores"] ? String(row["Modificadores"]) : "";
                        const rawParams = row["Parametros"] ? String(row["Parametros"]) : "";
                        const contentText = row["Contenido"] ? String(row["Contenido"]) : "";
                        const rawMustContain = row["Debe_Contener"] ? String(row["Debe_Contener"]).trim().toUpperCase() : "SI";
                        const mustContain = rawMustContain === "SI" || rawMustContain === "TRUE" || rawMustContain === "1";

                        const modifiers = rawModifiers
                            ? rawModifiers.split(",").map(s => s.trim()).filter(Boolean)
                            : [];
                        const parameters = rawParams
                            ? rawParams.split(",").map(s => s.trim()).filter(Boolean)
                            : [];

                        // Validate test against steps
                        const stepExists = stepsArr.some(s => s.paso === stepNum);
                        if (!stepExists && stepsArr.length > 0) {
                            warnings.push(`El test "${name}" está asignado al Paso ${stepNum}, pero ese paso no existe en la hoja 'Pasos'.`);
                        }

                        testsArr.push({
                            paso: stepNum,
                            name: name,
                            tipus: tipus,
                            entrada: entrada,
                            sortida: sortida,
                            methodName: methodName,
                            returnType: returnType,
                            modifiers: modifiers,
                            parameters: parameters,
                            contentText: contentText,
                            mustContain: mustContain
                        });
                    });
                } else {
                    warnings.push("No se encontró la hoja 'Tests'. Se creará la píldora sin tests unitarios iniciales.");
                }

                setParsedGeneral(genObj);
                setParsedSteps(stepsArr);
                setParsedTests(testsArr);
                setValidationErrors(errors);
                setValidationWarnings(warnings);
                setCurrentStep(1); // Move to review step
            } catch (err: any) {
                console.error("Error al procesar el archivo Excel:", err);
                message.error("Error al procesar el archivo Excel: formato no válido");
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // 3. Confirm and execute creation
    const handleConfirmImport = async () => {
        if (!parsedGeneral || parsedSteps.length === 0) {
            message.error("Datos insuficientes para crear la píldora");
            return;
        }

        setIsSubmitting(true);
        setSubmitProgress(10);
        setStatusMessage("Creando píldora y pasos...");

        try {
            // Serialize steps
            const serialisedInstrucciones = JSON.stringify(
                parsedSteps.map(s => ({
                    paso: s.paso,
                    titulo: s.titulo,
                    instrucciones: s.instrucciones
                }))
            );

            const jsonPayload = {
                id: 0,
                nombre: parsedGeneral.nombre,
                descripcion: parsedGeneral.descripcion,
                dificultad: parsedGeneral.dificultad,
                nivel: 1,
                instrucciones: serialisedInstrucciones,
                vida: 100,
                must: parsedGeneral.must,
                mustnot: parsedGeneral.mustnot,
                randomJavamon: parsedGeneral.rewardType === "JAVAMON_RANDOM",
                javamonId: 0,
                itemId: 0,
                rewardType: parsedGeneral.rewardType,
                tipo_recompensa: parsedGeneral.rewardType,
                reward_valor: parsedGeneral.reward_valor || null,
                intentos: parsedGeneral.intentos || 0,
                estat: parsedGeneral.estat,
                id_aula: null,
                url_teoria: parsedGeneral.url_teoria || ""
            };

            const formData = new FormData();
            formData.append("json", JSON.stringify(jsonPayload));

            const postResult: any = await PostCaza(formData);

            if (!postResult || !postResult.ok) {
                throw new Error("No se pudo crear la píldora base");
            }

            const createdId = postResult.id;
            setSubmitProgress(40);
            setStatusMessage(`Píldora creada (ID ${createdId || ""}). Configurando tests unitarios...`);

            // Si estamos importando dentro de un Conjunt de Pildores, vinculamos la pildora
            // recien creada a ese conjunt (tabla puente jutge.conjunt_caza), igual que al
            // crearla manualmente con defaultConjunt. Asi aparece automaticamente en el bloc.
            if (createdId && defaultConjunt != null) {
                try {
                    await fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${createdId}/conjunt`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id_conjunt: defaultConjunt, assigned: true })
                    });
                } catch (e) {
                    // No bloqueamos la importacion si falla la vinculacion; se avisa en consola.
                    console.error("No se pudo vincular la pildora al conjunt", e);
                    message.warning("La píldora se creó pero no se pudo vincular automáticamente al conjunt. Vincúlala manualmente.");
                }
            }

            // If we have an ID and tests, create the tests
            if (createdId && parsedTests.length > 0) {
                const totalTests = parsedTests.length;
                let processedTests = 0;

                for (const t of parsedTests) {
                    let fitxer = "";
                    let testName = t.name || `Paso ${t.paso} - Test`;

                    if (t.tipus === "es") {
                        const entrada = t.entrada || "";
                        const sortida = t.sortida || "";
                        fitxer = `ES:${btoa(entrada)}:${btoa(sortida)}`;
                    } else if (t.tipus === "method") {
                        const json = JSON.stringify({
                            methodName: t.methodName || "metodo",
                            modifiers: t.modifiers || ["public", "static"],
                            returnType: t.returnType || "void",
                            parameters: t.parameters || [],
                            entrada: t.entrada || "",
                            sortida: t.sortida || ""
                        });
                        fitxer = `METHOD:DATA:${btoa(json)}`;
                    } else if (t.tipus === "content") {
                        const json = JSON.stringify({
                            contentText: t.contentText || "",
                            mustContain: t.mustContain !== undefined ? t.mustContain : true
                        });
                        fitxer = `CONTENT:DATA:${btoa(json)}`;
                        testName = testName || "Content Check";
                    }

                    const testPayload = {
                        idCaza: parseInt(createdId),
                        tipus: t.tipus,
                        ordre: t.paso,
                        fitxer: fitxer,
                        name: testName
                    };

                    const testOk = await PutJutgeCazaTest(String(createdId), testPayload);
                    if (!testOk) {
                        // Desactivar píldora en backend si falló la creación de algún test
                        try {
                            await fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${createdId}/estat`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ estat_config: 0 })
                            });
                        } catch (cleanErr) {}
                        throw new Error(`Falló la creación del test '${testName}'. Se ha cancelado la importación para no dejar la píldora corrupta.`);
                    }

                    processedTests++;
                    const testProgress = 40 + Math.round((processedTests / totalTests) * 55);
                    setSubmitProgress(testProgress);
                    setStatusMessage(`Creando test ${processedTests} de ${totalTests}...`);
                }
            }

            setSubmitProgress(100);
            setStatusMessage("¡Importación completada con éxito!");
            message.success("¡Píldora importada con éxito desde Excel!");

            setTimeout(() => {
                resetState();
                onSuccess();
                onClose();
            }, 800);
        } catch (error: any) {
            console.error("Error durante la importación:", error);
            message.error(`Error en la importación: ${error?.message || "Error desconocido"}`);
            setIsSubmitting(false);
        }
    };

    // Columns for Tests Table in Preview
    const testColumns = [
        {
            title: "Paso",
            dataIndex: "paso",
            key: "paso",
            width: 70,
            render: (val: number) => <Tag color="blue">Paso {val}</Tag>
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            render: (val: string) => <Text strong>{val}</Text>
        },
        {
            title: "Tipo",
            dataIndex: "tipus",
            key: "tipus",
            width: 100,
            render: (val: string) => {
                if (val === "method") return <Tag color="purple"><FunctionOutlined /> Método</Tag>;
                if (val === "content") return <Tag color="green"><FileTextOutlined /> Código</Tag>;
                return <Tag color="cyan"><CodeOutlined /> E/S Consola</Tag>;
            }
        },
        {
            title: "Detalles del Test",
            key: "details",
            render: (_: any, record: ParsedTest) => {
                if (record.tipus === "method") {
                    return (
                        <div style={{ fontSize: 12 }}>
                            <code>{record.modifiers?.join(" ")} {record.returnType} {record.methodName}({record.parameters?.join(", ")})</code>
                            {record.sortida && <div style={{ color: "#52c41a" }}>Salida esperada: {record.sortida}</div>}
                        </div>
                    );
                }
                if (record.tipus === "content") {
                    return (
                        <div style={{ fontSize: 12 }}>
                            {record.mustContain ? "Debe contener: " : "NO debe contener: "}
                            <Tag>{record.contentText}</Tag>
                        </div>
                    );
                }
                return (
                    <div style={{ fontSize: 12 }}>
                        {record.entrada && <div><b>Entrada:</b> <code>{record.entrada.replace(/\n/g, " ↵ ")}</code></div>}
                        {record.sortida && <div><b>Salida:</b> <code>{record.sortida.replace(/\n/g, " ↵ ")}</code></div>}
                    </div>
                );
            }
        }
    ];

    return (
        <Modal
            title={
                <Space align="center">
                    <FileExcelOutlined style={{ color: "#217346", fontSize: 22 }} />
                    <span style={{ fontSize: 18, fontWeight: 600 }}>Crear Píldora desde Excel</span>
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            width={850}
            footer={null}
            destroyOnClose
        >
            <Steps
                current={currentStep}
                style={{ marginBottom: 24, marginTop: 8 }}
                items={[
                    { title: "Subir Archivo", description: "Excel (.xlsx)" },
                    { title: "Verificar Datos", description: "Revisión y Pasos" },
                    { title: "Creación", description: "Guardado final" }
                ]}
            />

            {/* STEP 0: Upload & Download Template */}
            {currentStep === 0 && (
                <div>
                    <Alert
                        type="info"
                        showIcon
                        message="Carga masiva de Píldoras Formativas"
                        description={
                            <div>
                                Puedes definir en un archivo Excel todos los pasos, enunciados y pruebas unitarias de la píldora.
                                Si aún no tienes el archivo, descarga la plantilla con ejemplos listos para rellenar.
                                <br />
                                <i>Nota: Las imágenes (monstruo y fondo) no se cargan vía Excel; se asignan automáticamente de forma predeterminada y podrás cambiarlas editando la píldora.</i>
                            </div>
                        }
                        style={{ marginBottom: 20 }}
                    />

                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                        <Button
                            type="dashed"
                            size="large"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadTemplate}
                            style={{ borderColor: "#217346", color: "#217346" }}
                        >
                            Descargar Plantilla Excel (.xlsx)
                        </Button>
                    </div>

                    <Dragger
                        name="excel"
                        multiple={false}
                        accept=".xlsx, .xls"
                        beforeUpload={(file) => {
                            processExcelFile(file);
                            return false;
                        }}
                        showUploadList={false}
                        style={{
                            padding: "30px 20px",
                            background: "#fafafa",
                            border: "2px dashed #d9d9d9",
                            borderRadius: 12
                        }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ color: "#217346", fontSize: 48 }} />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 500 }}>
                            Haz clic o arrastra tu archivo Excel aquí
                        </p>
                        <p className="ant-upload-hint" style={{ color: "#8c8c8c" }}>
                            Soporta archivos .xlsx con hojas: General, Pasos y Tests.
                        </p>
                    </Dragger>
                </div>
            )}

            {/* STEP 1: Verification Screen (Pantalla intermedia) */}
            {currentStep === 1 && (
                <div>
                    {validationErrors.length > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                            message="Errores que impiden la creación"
                            description={
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {validationErrors.map((err, i) => (
                                        <li key={i}>{err}</li>
                                    ))}
                                </ul>
                            }
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {validationWarnings.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Avisos detectados en el archivo"
                            description={
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {validationWarnings.map((warn, i) => (
                                        <li key={i}>{warn}</li>
                                    ))}
                                </ul>
                            }
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {validationErrors.length === 0 && (
                        <Alert
                            type="success"
                            showIcon
                            icon={<CheckCircleOutlined />}
                            message="Datos verificados correctamente"
                            description="Revisa los pasos y pruebas detectados antes de confirmar la creación."
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* General Summary */}
                    {parsedGeneral && (
                        <Card
                            size="small"
                            title={<span style={{ fontWeight: 600 }}>Información General de la Píldora</span>}
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        >
                            <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
                                <Descriptions.Item label="Nombre" span={2}>
                                    <Text strong>{parsedGeneral.nombre}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Dificultad">
                                    <Tag color={parsedGeneral.dificultad === "Expert" ? "red" : parsedGeneral.dificultad === "Intermediate" ? "orange" : "green"}>
                                        {parsedGeneral.dificultad}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Intentos">
                                    {parsedGeneral.intentos > 0 ? (
                                        <Tag color="blue">{parsedGeneral.intentos} intentos</Tag>
                                    ) : (
                                        <Tag color="green">Ilimitados</Tag>
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Recompensa" span={2}>
                                    <Tag color="gold">
                                        {parsedGeneral.rewardType === "PUNTUACION" 
                                            ? `Puntos (${parsedGeneral.reward_valor || 10} pts)` 
                                            : parsedGeneral.rewardType}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Palabras Obligatorias (Must)" span={2}>
                                    {parsedGeneral.must ? (
                                        parsedGeneral.must.split(",").map((t, idx) => (
                                            <Tag color="cyan" key={idx}>{t.trim()}</Tag>
                                        ))
                                    ) : (
                                        <Text type="secondary">Ninguna</Text>
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Palabras Prohibidas (Must Not)" span={3}>
                                    {parsedGeneral.mustnot ? (
                                        parsedGeneral.mustnot.split(",").map((t, idx) => (
                                            <Tag color="volcano" key={idx}>{t.trim()}</Tag>
                                        ))
                                    ) : (
                                        <Text type="secondary">Ninguna</Text>
                                    )}
                                </Descriptions.Item>
                                {parsedGeneral.url_teoria && (
                                    <Descriptions.Item label="URL Teoría" span={3}>
                                        <a href={parsedGeneral.url_teoria} target="_blank" rel="noopener noreferrer">
                                            {parsedGeneral.url_teoria}
                                        </a>
                                    </Descriptions.Item>
                                )}
                                {parsedGeneral.descripcion && (
                                    <Descriptions.Item label="Descripción" span={3}>
                                        <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2, expandable: true, symbol: "más" }}>
                                            {parsedGeneral.descripcion}
                                        </Paragraph>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>
                    )}

                    {/* Steps & Instructions Collapse */}
                    <Card
                        size="small"
                        title={
                            <Space>
                                <span style={{ fontWeight: 600 }}>Pasos Detectados ({parsedSteps.length})</span>
                                <Badge count={`${parsedTests.length} tests en total`} style={{ backgroundColor: "#52c41a" }} />
                            </Space>
                        }
                        style={{ marginBottom: 16, borderRadius: 8 }}
                    >
                        {parsedSteps.length === 0 ? (
                            <Text type="secondary">No se detectaron pasos.</Text>
                        ) : (
                            <Collapse defaultActiveKey={["step-0"]}>
                                {parsedSteps.map((step, idx) => {
                                    const stepTests = parsedTests.filter(t => t.paso === step.paso);
                                    return (
                                        <Panel
                                            key={`step-${idx}`}
                                            header={
                                                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                                                    <span>
                                                        <Tag color="blue">Paso {step.paso}</Tag>
                                                        <Text strong>{step.titulo}</Text>
                                                    </span>
                                                    <Tag color={stepTests.length > 0 ? "green" : "default"}>
                                                        {stepTests.length} tests
                                                    </Tag>
                                                </Space>
                                            }
                                        >
                                            <div style={{ marginBottom: 12 }}>
                                                <Text type="secondary">Enunciado / Instrucciones:</Text>
                                                <pre style={{
                                                    background: "#f8f9fa",
                                                    padding: "8px 12px",
                                                    borderRadius: 6,
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                    marginTop: 4,
                                                    fontSize: 13,
                                                    fontFamily: "inherit"
                                                }}>
                                                    {step.instrucciones || "(Sin instrucciones)"}
                                                </pre>
                                            </div>

                                            {stepTests.length > 0 && (
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>Tests asociados al paso:</Text>
                                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                                        {stepTests.map((st, sIdx) => (
                                                            <Tag key={sIdx} color="purple">
                                                                {st.tipus.toUpperCase()}: {st.name}
                                                            </Tag>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Panel>
                                    );
                                })}
                            </Collapse>
                        )}
                    </Card>

                    {/* Tests Detail Table */}
                    {parsedTests.length > 0 && (
                        <Card
                            size="small"
                            title={<span style={{ fontWeight: 600 }}>Detalle de Tests Unitarios ({parsedTests.length})</span>}
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        >
                            <Table
                                dataSource={parsedTests}
                                columns={testColumns}
                                rowKey={(_, idx) => `test-${idx}`}
                                size="small"
                                pagination={{ pageSize: 5 }}
                            />
                        </Card>
                    )}

                    {/* Actions */}
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => setCurrentStep(0)}
                            disabled={isSubmitting}
                        >
                            Volver y cambiar archivo
                        </Button>

                        <Space>
                            <Button onClick={handleClose} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                icon={<RocketOutlined />}
                                onClick={handleConfirmImport}
                                disabled={validationErrors.length > 0}
                                loading={isSubmitting}
                                style={{ background: "#217346", borderColor: "#217346" }}
                            >
                                Confirmar y Crear Píldora
                            </Button>
                        </Space>
                    </div>
                </div>
            )}

            {/* STEP 2: Submission Progress */}
            {isSubmitting && (
                <div style={{ textAlign: "center", padding: "30px 20px" }}>
                    <Progress
                        type="circle"
                        percent={submitProgress}
                        status={submitProgress === 100 ? "success" : "active"}
                        strokeColor={{ "0%": "#108ee9", "100%": "#217346" }}
                        size={100}
                    />
                    <Title level={4} style={{ marginTop: 20 }}>
                        {statusMessage}
                    </Title>
                    <Text type="secondary">
                        Por favor espera mientras se procesa y registran los pasos y pruebas unitarias.
                    </Text>
                </div>
            )}
        </Modal>
    );
};

export default ImportarCazaExcelModal;
